// apps/console/src/services/api/domains/robot-media-resolve.api.ts
//
// The resolvers that turn an opaque robot media key into a short-lived presigned GET URL. Split out
// of robots.api.ts only to keep that file inside the 300-line rule; `robotsApi` spreads these
// methods in, so callers (and specs that spy on `robotsApi`) use them from one place.
//
// There is one resolver per FOLDER, and the separation is the security property: each accepts only
// its own folder, so a helmet key cannot be laundered through the snapshot resolver, a snapshot key
// cannot be laundered through the helmet one, and a map ref cannot be laundered through either.

import { api } from '../client'

export const robotMediaResolveApi = {
  /**
   * Resolve an opaque robot media key (`robots/<tenant|central>/snapshots/<uuid>.<ext>`)
   * to a short-lived presigned GET url (C2). Cognito-auth + read-role; the
   * backend re-validates the key against the operator's tenant (the sanitizer +
   * tenant gate), so a foreign-tenant / `javascript:` / `data:` / absolute-URL
   * ref is rejected (400). Used by `<AlertSnapshot>` to render the face crop when it is the only
   * thumbnail asked for at that moment; several at once go through `resolveSnapshotBatch`.
   */
  resolveSnapshot: (ref: string) => api.post<{ url: string }>('/robots/snapshots/resolve', { ref }),

  /**
   * Resolve many snapshot / gallery keys in a single call (at most 100 refs).
   *
   * The same per-tenant gate as `resolveSnapshot`, applied to every ref, and no `robotId` — a
   * snapshot belongs to the tenant. A ref the gate refuses is simply absent from `urls`; the
   * response never says which part of it was wrong. Used by the snapshot batcher in
   * `robot-media-batch.ts`, so an alert log's thumbnails cost one request instead of one each.
   */
  resolveSnapshotBatch: (refs: string[]) =>
    api.post<{ urls: Record<string, string> }>('/robots/snapshots/resolve-batch', { refs }),

  /**
   * Resolve one helmet capture to a short-lived presigned GET URL.
   *
   * `robotId` narrows the gate further than the tenant — a capture belongs to one worker's device,
   * not to the tenant at large.
   */
  resolveMedia: (ref: string, robotId?: string) =>
    api.post<{ url: string }>('/robots/media/resolve', { ref, ...(robotId ? { robotId } : {}) }),

  /**
   * Resolve many captures of ONE device in a single call (at most 100 refs).
   *
   * The same per-tenant, per-device gate as `resolveMedia`, applied to every ref. A ref the gate
   * refuses is simply absent from `urls` — the response never says which part of it was wrong.
   */
  resolveMediaBatch: (robotId: string, refs: string[]) =>
    api.post<{ urls: Record<string, string> }>('/robots/media/resolve-batch', { robotId, refs }),

  /** Resolve a stored map key (`robots/<tenant|central>/maps/<uuid>.json`) to a short-lived presigned GET url. */
  resolveMap: (ref: string) => api.post<{ url: string }>('/robots/maps/resolve', { ref }),
}
